import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FieldCheckbox, Icons } from '../../../components';
import css from './SelectMultipleFilter.module.css';
import { colorOptions as fallbackColorOptions } from '../../../config/marketplace-custom-config';
import { useConfiguration } from '../../../context/configurationContext';
import { getListingFieldOptions } from '../../../config/marketplaceConfig';
import { ListingFieldConfigKey } from '../../../util/enums';
import { normalizeColorSwatchKey, resolveColorSwatch } from './colorSwatches';

// 1. Helper Component
const ColorItem = ({ id, name, color }) => {
  const { key, label, icon, swatchStyle } = color;
  const fieldId = `${id}.${key}`;

  return (
    <li className={css.colorItem}>
      <label htmlFor={fieldId}>
        {icon ? (
          <img src={icon} alt={label} className={css.colorIcon} />
        ) : (
          <span className={css.colorSwatch} style={swatchStyle} aria-hidden="true" />
        )}
      </label>
      <FieldCheckbox
        id={fieldId}
        name={name}
        label={
          <span className={css.colorLabel}>
            {label} <Icons name="checkMarkColor" />
          </span>
        }
        value={key}
      />
    </li>
  );
};

ColorItem.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  color: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    swatchStyle: PropTypes.shape({
      background: PropTypes.string,
      borderColor: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

// 2. Main Component
// We destructure props here and set 'containerClass = null' directly.
function RenderColors({ id, name, containerClass = null }) {
  const config = useConfiguration();
  const configuredOptions = getListingFieldOptions(ListingFieldConfigKey.COLOR, { config });

  const colorChoices = useMemo(() => {
    const iconMap = (fallbackColorOptions || []).reduce((acc, item) => {
      const keyFromOption = item?.key ? normalizeColorSwatchKey(item.key) : null;
      const keyFromLabel = item?.label ? normalizeColorSwatchKey(item.label) : null;
      if (keyFromOption && item?.icon) {
        acc[keyFromOption] = item.icon;
      }
      if (keyFromLabel && item?.icon) {
        acc[keyFromLabel] = item.icon;
      }
      return acc;
    }, {});

    return (configuredOptions || [])
      .map(option => {
        const key = option?.option != null ? `${option.option}` : '';
        const label = option?.label != null ? `${option.label}` : key;
        if (!key || !label) {
          return null;
        }

        const optionKey = normalizeColorSwatchKey(key);
        const labelKey = normalizeColorSwatchKey(label);
        const icon = iconMap[optionKey] || iconMap[labelKey] || null;

        return {
          key,
          label,
          icon,
          swatchStyle: resolveColorSwatch(key || label),
        };
      })
      .filter(Boolean);
  }, [configuredOptions]);

  return (
    <fieldset className={containerClass}>
      <ul className={css.list}>
        {colorChoices.map(color => (
          <ColorItem key={`${id}.${color.key}`} id={id} name={name} color={color} />
        ))}
      </ul>
    </fieldset>
  );
}

RenderColors.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  containerClass: PropTypes.string,
};

// 3. Export with memoization
export default memo(RenderColors);
